import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // your axios instance
import { showLoader, hideLoader } from "./loaderSlice";

export const getAllBreeds = createAsyncThunk(
  "breed/getAllBreeds",
  async (speciesId, thunkAPI) => {
    try {
      if (!speciesId) throw new Error("speciesId is required");

      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/getall/clinic/species/${speciesId}/breeds`
      );
      thunkAPI.dispatch(hideLoader());

      if (res.status === 200 || res.status === 201) {
        return res.data.data;
      }
      return thunkAPI.rejectWithValue("Failed to fetch breeds");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getSpeciesListForDropdown = createAsyncThunk(
  "breed/getSpeciesListForDropdown",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get("/clinic/species");
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        return res.data.data;
      }
      return thunkAPI.rejectWithValue("Failed to fetch species list");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addBreed = createAsyncThunk(
  "breed/addBreed",
  async ({ speciesId, breedName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(`/create/species/${speciesId}/breed?breedName=${encodeURIComponent(breedName)}`);
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        // Return the breed data with speciesId included
        return {
          ...res.data.data,
          speciesId: speciesId
        };
      }
      return thunkAPI.rejectWithValue("Failed to add breed");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateBreed = createAsyncThunk(
  "breed/updateBreed",
  async ({ breedId, breedName, speciesId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      
      // If speciesId is provided, you might need to update the API call
      // to include species information. For now, keeping the existing API call.
      const res = await api({ contentType: true, auth: true }).put(`/update/breed/${breedId}?breedName=${encodeURIComponent(breedName)}`);
      
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 201) {
        // Return the updated breed data with speciesId if provided
        return {
          ...res.data.data,
          speciesId: speciesId || res.data.data.speciesId
        };
      }
      return thunkAPI.rejectWithValue("Failed to update breed");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteBreed = createAsyncThunk(
  "breed/deleteBreed",
  async (breedId, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(`/delete/breed/${breedId}`);
      thunkAPI.dispatch(hideLoader());
      if (res.status === 200 || res.status === 204) {
        return breedId;
      }
      return thunkAPI.rejectWithValue("Failed to delete breed");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

const breedSlice = createSlice({
  name: "breed",
  initialState: {
    breeds: [],
    speciesOptions: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllBreeds.fulfilled, (state, action) => {
        state.breeds = Array.isArray(action.payload) ? action.payload : [];
        state.loading = false;
        state.error = null;
      })
      .addCase(getSpeciesListForDropdown.fulfilled, (state, action) => {
        // Store the full species data, not just names
        state.speciesOptions = Array.isArray(action.payload) ? action.payload : [];
        state.loading = false;
        state.error = null;
      })
      .addCase(addBreed.fulfilled, (state, action) => {
        state.breeds.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateBreed.fulfilled, (state, action) => {
        const index = state.breeds.findIndex((b) => b.breedId === action.payload.breedId);
        if (index !== -1) {
          state.breeds[index] = { ...state.breeds[index], ...action.payload };
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteBreed.fulfilled, (state, action) => {
        state.breeds = state.breeds.filter((b) => b.breedId !== action.payload);
        state.loading = false;
        state.error = null;
      })
      .addMatcher(
        (action) =>
          action.type.startsWith("breed/") &&
          action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("breed/") &&
          action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || action.error.message;
        }
      );
  },
});

export default breedSlice.reducer;